// Authenticated: flags live on the user (auth store), fetched via fetchUser().
// Guest: flags are fetched from the features API and cached for the session.

import { defineStore } from 'pinia'
import { featuresApi } from '@/api/index.js'
import { useAuthStore } from '@/store/auth.js'
import type { FeatureFlags } from '@/types/features.ts'

interface FeaturesState {
  features: FeatureFlags | null
}

// In-flight guest request — lives outside the store: it is not reactive
// state, just a dedup guard so concurrent load() calls share one request.
let pendingRequest: Promise<FeatureFlags | null> | null = null

export const useFeaturesStore = defineStore('features', {
  state: (): FeaturesState => ({
    features: null
  }),

  getters: {
    isJobsPageEnabled: (state): boolean => !!state.features?.profile?.rt?.['jobs-page'],
    isVacancyPreviewEnabled: (state): boolean => !!state.features?.vacancy?.preview,
    isHelpPageEnabled: (state): boolean => !!state.features?.profile?.help
  },

  actions: {
    // Idempotent: resolves the flags once, concurrent calls share one request.
    async load(): Promise<FeatureFlags | null> {
      const authStore = useAuthStore()

      // Authenticated — always read from the user, so a re-login picks up fresh flags
      if (authStore.isAuthenticated()) {
        if (!authStore.user?.features) await authStore.fetchUser()
        this.features = authStore.user?.features ?? null
        return this.features
      }

      // Guest — fetch once, cache for the session
      if (this.features) return this.features

      pendingRequest ??= featuresApi.get()
        .then(({ data: { data } }: { data: { data: FeatureFlags } }) => {
          this.features = data
          return this.features
        })
        .finally(() => {
          pendingRequest = null
        })

      return pendingRequest
    }
  }
})
